import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Reads this trigger's section-scoped Quick Add config, serialized by
   * snippets/quick-add.liquid as JSON in data-quick-add-config on the closest
   * quick-add-component (this element).
   * @returns {object}
   */
  #getSectionConfig() {
    try {
      return JSON.parse(this.dataset.quickAddConfig || '{}');
    } catch {
      return {};
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const config = this.#getSectionConfig();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#applyLabelOverrides(config.labels);
    }

    this.#openQuickAddModal(config);
  };

  /**
   * Best-effort text substitution of the morphed PDP content's built-in labels with the
   * triggering section's Quick Add label overrides. The fetched content otherwise uses
   * the theme's global locale strings, which are identical for every section.
   * @param {Record<string, string> | undefined} labels
   */
  #applyLabelOverrides(labels) {
    if (!labels) return;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const addToCartText = modalContent.querySelector('.add-to-cart-text__content span span');
    if (addToCartText && labels.addToCart) addToCartText.textContent = labels.addToCart;

    const submitButton = modalContent.querySelector('add-to-cart-component button[type="submit"]');
    if (submitButton?.hasAttribute('disabled') && labels.soldOut) {
      const label = submitButton.querySelector('.add-to-cart-text__content span span');
      if (label) label.textContent = labels.soldOut;
    }

    const quantityLabel = modalContent.querySelector('.quantity-selector label, [data-quantity-label]');
    if (quantityLabel && labels.quantity) quantityLabel.textContent = labels.quantity;

    // Note: intentionally NOT touching `.view-product-title a` -- that is the product
    // title itself, not a "view details" affordance, and must never be overwritten.
    const viewDetailsLink = modalContent.querySelector('.view-more-details');
    if (viewDetailsLink && labels.viewDetails && !viewDetailsLink.querySelector('img, svg')) {
      viewDetailsLink.textContent = labels.viewDetails;
    }
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * @param {object} [config] - This trigger's section-scoped Quick Add config
   */
  #openQuickAddModal = (config) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.applySectionConfig(config || {});

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {object} The config of the section whose card most recently opened this modal */
  currentConfig = {};

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Applies the triggering section's Quick Add configuration (presentation, styling,
   * labels) to this shared modal instance. Called every time a Quick Add trigger is
   * clicked, immediately before the dialog opens, so the same shared modal always
   * reflects whichever section's card was used to open it.
   * @param {object} config
   */
  applySectionConfig(config) {
    this.currentConfig = config || {};
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    const presentation = this.currentConfig.presentation || 'bottom_sheet';
    dialog.classList.remove('quick-add-modal--center_modal', 'quick-add-modal--side_drawer', 'quick-add-modal--bottom_sheet');
    dialog.classList.add(`quick-add-modal--${presentation}`);

    dialog.classList.toggle('quick-add-modal--align-center', this.currentConfig.contentAlignment === 'center');
    dialog.classList.toggle('quick-add-modal--align-left', this.currentConfig.contentAlignment !== 'center');

    dialog.style.setProperty('--quick-add-modal-custom-width', `${this.currentConfig.popupWidth ?? 700}px`);
    dialog.style.setProperty('--quick-add-modal-custom-radius', `${this.currentConfig.popupRadius ?? 0}px`);
    dialog.style.setProperty(
      '--quick-add-modal-shadow',
      this.currentConfig.popupShadow === false ? 'none' : '0 5px 30px rgb(0 0 0 / var(--opacity-15))'
    );

    if (this.currentConfig.useCustomColors) {
      if (this.currentConfig.popupBg) dialog.style.setProperty('--quick-add-modal-bg', this.currentConfig.popupBg);
      if (this.currentConfig.popupTextColor) dialog.style.setProperty('--quick-add-modal-text', this.currentConfig.popupTextColor);
      if (this.currentConfig.popupBorderColor) dialog.style.setProperty('--quick-add-modal-border', this.currentConfig.popupBorderColor);
    } else {
      dialog.style.removeProperty('--quick-add-modal-bg');
      dialog.style.removeProperty('--quick-add-modal-text');
      dialog.style.removeProperty('--quick-add-modal-border');
    }

    if (this.currentConfig.labels?.close) {
      this.refs.closeButton?.setAttribute('aria-label', this.currentConfig.labels.close);
    }
  }

  /**
   * Closes the dialog on a successful add, unless the triggering section's Quick Add
   * config requests staying open with a success message instead.
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;

    if (this.currentConfig.successBehavior === 'stay') {
      this.#showStaySuccessMessage();
      return;
    }

    this.closeDialog();
  };

  #showStaySuccessMessage() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    let successEl = modalContent.querySelector('.quick-add-success-message');
    if (!successEl) {
      successEl = document.createElement('p');
      successEl.className = 'quick-add-success-message';
      successEl.setAttribute('role', 'status');
      successEl.setAttribute('aria-live', 'polite');
      modalContent.prepend(successEl);
    }
    successEl.textContent = this.currentConfig.labels?.success || 'Product successfully added';
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
